import { Injectable } from '@angular/core';
import { IonContent, Platform } from '@ionic/angular';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { keyboardWillClose, keyboardWillOpen, selectKeyboardIsOpen } from '~/modules/app/slice';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'any'
})
export class ScrollControllerService {
  private ionContent: IonContent;
  private contentElement: HTMLElement = null;
  private readonly destroyed$ = new Subject<boolean>();
  private disableAutoScrollOnKeyboardOpen = false;

  constructor(
    private readonly actions: Actions,
    private readonly store: Store,
    private readonly platform: Platform
  ) {}

  async getScrollY(): Promise<number> {
    if (!this.getIonContent()) {
      return Promise.resolve(0);
    }
    const scrollElement = await this.ionContent.getScrollElement().catch();
    if (!scrollElement) {
      return Promise.resolve(0);
    }
    this.contentElement = scrollElement;
    return Promise.resolve(scrollElement.scrollTop);
  }

  elementInViewPortInfo(element: HTMLElement): { isInViewPort: boolean; direction: 'top' | 'bottom'; isVisible: boolean } {
    const contentComponent: IonContent = this.getIonContent();

    if (!contentComponent || !element) {
      return {
        isVisible: true,
        isInViewPort: true,
        direction: null
      };
    }

    const contentHeight = (contentComponent as any).el.offsetHeight;
    const diffTop = (window.outerHeight - contentHeight) / 2;
    const rect = element.getBoundingClientRect();
    const isVisible = contentHeight > rect.top - 2 * rect.height;
    this.contentElement = (contentComponent as any).el;

    if (rect.top < diffTop) {
      return {
        isVisible,
        isInViewPort: false,
        direction: 'top'
      };
    }
    if (rect.top > contentHeight) {
      return {
        isVisible,
        isInViewPort: false,
        direction: 'bottom'
      };
    }
    return {
      isVisible,
      isInViewPort: true,
      direction: null
    };
  }

  onScrollY(): Observable<{ scrollTop: number; direction: number }> {
    if (!this.ionContent) {
      return EMPTY;
    }
    this.ionContent.scrollEvents = true;
    return this.ionContent.ionScroll.pipe(
      switchMap((event) =>
        of({
          scrollTop: event.detail.scrollTop,
          direction: event.detail.deltaY > 0 ? 1 : event.detail.deltaY === 0 ? 0 : -1
        })
      )
    );
  }

  scrollToBottom(duration?: number): Promise<void> {
    const contentComponent: IonContent = this.getIonContent();
    const durationDefault = duration === undefined ? 250 : duration;
    if (contentComponent) {
      return contentComponent.scrollToBottom(durationDefault).catch();
    }
    return Promise.resolve();
  }

  scrollY(y: number, duration?: number): Promise<void> {
    const contentComponent: IonContent = this.getIonContent();
    const durationDefault = duration === undefined ? 250 : duration;
    if (contentComponent) {
      return contentComponent.scrollByPoint(0, y, durationDefault).catch();
    }
    return Promise.resolve();
  }

  scrollToY(y: number, duration?: number): Promise<void> {
    const contentComponent: IonContent = this.getIonContent();
    const durationDefault = duration === undefined ? 250 : duration;
    if (contentComponent) {
      return contentComponent.scrollToPoint(0, y, durationDefault).catch();
    }
    return Promise.resolve();
  }

  scrollToElement(element: HTMLElement, duration?: number): Promise<void> {
    const contentComponent: IonContent = this.getIonContent();
    const durationDefault = duration === undefined ? 250 : duration;
    if (contentComponent) {
      return contentComponent.scrollToPoint(0, element.offsetTop - 16, durationDefault).catch();
    }
    return Promise.resolve();
  }

  scrollOffsetTopIfNeeded(offsetY: number, element: HTMLElement, duration?: number, marginTop?: number): Promise<void> {
    return new Promise<void>((resolver) => {
      const contentComponent: IonContent = this.getIonContent();
      const durationDefault = duration === undefined ? 250 : duration;
      const marginY = marginTop || 0;
      if (contentComponent) {
        contentComponent.getScrollElement().then((e) => {
          this.contentElement = e;
          if (offsetY > 0 && element.offsetTop - e.scrollTop > offsetY) {
            const y = element.offsetTop - e.scrollTop - offsetY;
            contentComponent.scrollByPoint(0, y - marginY, durationDefault).then();
          } else if (element.offsetTop - e.scrollTop < 0) {
            const y = element.offsetTop - e.scrollTop;
            contentComponent.scrollByPoint(0, y - marginY, durationDefault).then();
          } else if (element.offsetTop - e.scrollTop > e.offsetHeight - element.offsetHeight) {
            const delta = element.offsetTop - e.scrollTop - e.offsetHeight;
            contentComponent.scrollByPoint(0, Math.abs(delta * 2) - marginY, durationDefault).then();
          }
          resolver();
        });
      } else {
        resolver();
      }
    });
  }

  setIonContent(content: IonContent, enableAutoScrollOnKeyboardOpen?: boolean): ScrollControllerService {
    if (!this.platform.is('hybrid')) {
      return this;
    }
    this.disableAutoScrollOnKeyboardOpen = !enableAutoScrollOnKeyboardOpen;
    if (content) {
      this.ionContent = content;
      this.observeIonContentElement();
    }
    return this;
  }

  destroy() {
    this.destroyed$.next(true);
  }

  getIonContent(): IonContent | null {
    return !this.ionContent || !this.ionContent.ionScroll ? null : this.ionContent;
  }

  getIonContentElement(): HTMLElement | null {
    return this.contentElement || (this.getIonContent() as any)?.el;
  }

  private observeIonContentElement() {
    if (!this.ionContent) {
      return;
    }
    let tabsHeight = 0;
    let lastScrollY = 0;
    this.ionContent.getScrollElement().then((scrollElement) => {
      const tabs = document.getElementsByTagName('ion-tab-bar');
      for (let i = 0; i < tabs.length; i++) {
        const tabHeight = tabs.item(i).getBoundingClientRect().height;
        if (tabHeight) {
          tabsHeight = tabHeight;
        }
      }
      this.actions
        .pipe(takeUntil(this.destroyed$), ofType(keyboardWillOpen, keyboardWillClose))
        .pipe(
          switchMap(async (action) => {
            const keyboardHeight = action.height || 0;
            if (action.type === keyboardWillOpen.type) {
              lastScrollY = scrollElement.scrollTop;
            }
            return keyboardHeight || 0;
          }),
          withLatestFrom(this.store.select(selectKeyboardIsOpen))
        )
        .subscribe(async ([keyboardHeight, isOpen]) => {
          // (Note: body-resizing on keyboard open/close is now handled app-wide by KeyboardBodyResizeService)

          if (!this.disableAutoScrollOnKeyboardOpen) {
            if (!isOpen || !keyboardHeight) {
              if (keyboardHeight) {
                this.scrollY(keyboardHeight - tabsHeight);
              } else if (lastScrollY) {
                this.scrollToY(lastScrollY, 250).finally(() => {
                  this.scrollToY(lastScrollY, 0);
                });
              }
            }
          }
        });
    });
  }
}
